import React, { Suspense } from 'react';
import lazyWithRetry from 'utils/lazyWithRetry';
import { Navigate } from 'react-router-dom';
import NotFound from 'components/404';
import Loader from 'wrapper/loader';
import TenantGate from 'wrapper/tenant-gate';

const SessionExpire = lazyWithRetry(() => import('wrapper/session-expire/SessionExpire'));
const InviteExpire = lazyWithRetry(() => import('wrapper/invite-expire'));
const EmailVerified = lazyWithRetry(() => import('wrapper/email-verified'));
const VerifyPassword = lazyWithRetry(() => import('pages/verify-password'));
const StytchCallback = lazyWithRetry(() => import('pages/auth/StytchCallback'));
const VerifyEmail = lazyWithRetry(() => import('wrapper/verify-email'));
const PersonalInfoCard = lazyWithRetry(() => import('wrapper/personal-info'));
const UnifiedLogin = lazyWithRetry(() => import('pages/auth/UnifiedLogin'));
const SpecialistSsoLogin = lazyWithRetry(() => import('pages/auth/SpecialistSsoLogin'));
const LoginPrompt = lazyWithRetry(() => import('wrapper/login-page'));
const SsoLoginPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoLogin'));
const SsoLoadingPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoLoading'));
const SsoSignedOutPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoSignedOut'));
const SsoUnknownTenantPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoUnknownTenant'));
const SsoVerifyPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoVerify'));
const SsoVerifyingPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoVerifying'));
const SsoUnauthorizedPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoUnauthorized'));
const SsoNoResponsePreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoNoResponse'));
const SsoExpiredPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoExpired'));
const SsoUnknownPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoUnknown'));
const SsoWelcomeBackPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoWelcomeBack'));
const SsoWelcomeNewPreview = lazyWithRetry(() => import('pages/auth/sso-v2/SsoWelcomeNew'));
const authRoutes = [
  // ── Tenant-gated routes ──────────────────────────────────────────
  // These validate org/branch slug from the hostname before rendering.
  {
    element: <TenantGate />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <LoginPrompt />
          </Suspense>
        ),
      },
      // Primary B2B login page — the Figma-aligned card with the SSO
      // button only (no magic-link form).
      {
        path: '/login/sso',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="specialist" ssoOnly />
          </Suspense>
        ),
      },
      // Specialist-only variant of the SSO card: same page, Home buyer
      // choice removed, SSO button directly below the Specialist chip.
      {
        path: '/login/sso/specialist',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SpecialistSsoLogin />
          </Suspense>
        ),
      },
      // Magic-link B2B login — unlisted; kept for manual/internal access.
      {
        path: '/login/b2b',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="specialist" />
          </Suspense>
        ),
      },
      // B2C consumer magic-link login page
      {
        path: '/login/consumer',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="buyer" />
          </Suspense>
        ),
      },
      // Backwards-compatible entrypoint for direct testing
      {
        path: '/stytch-login',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <UnifiedLogin initialUserType="specialist" />
          </Suspense>
        ),
      },
      {
        path: '/login',
        element: <Navigate to="/login/consumer" replace />,
      },
      // SSO login (Cognito-brokered). Frontend pages are wired; backend
      // exchange via /auth/sso/initiate + /auth/sso/callback is WIP.
      // Each visible state is its own route so it can be linked / designed independently.
      {
        path: '/login-sso',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoLoginPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/loading',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoLoadingPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/signed-out',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoSignedOutPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/unknown-tenant',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoUnknownTenantPreview />
          </Suspense>
        ),
      },
      {
        // Live Cognito callback. Cognito redirects here with ?code=&state=
        // after the IdP handshake; SsoVerifying POSTs to /auth/sso/callback
        // and routes the user to the resulting redirect_to page.
        path: '/login-sso/verify',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoVerifyingPreview />
          </Suspense>
        ),
      },
      {
        // Back-compat alias — previously the canonical callback path. Kept so
        // any stale Cognito whitelist entry or saved link still works.
        path: '/login-sso/verifying',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoVerifyingPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/unauthorized',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoUnauthorizedPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/no-response',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoNoResponsePreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/expired',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoExpiredPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/unknown',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoUnknownPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/welcome-back',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoWelcomeBackPreview />
          </Suspense>
        ),
      },
      {
        path: '/login-sso/welcome',
        element: (
          <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
            <SsoWelcomeNewPreview />
          </Suspense>
        ),
      },
    ],
  },

  // ── Ungated routes ───────────────────────────────────────────────
  // Callbacks and legacy flows must work regardless of tenant validation.
  //
  // NOTE: The /embed and /embed/documents iframe-embed routes used to live
  // here but have been hoisted to routes/pages/embed.jsx so they can be
  // mounted ABOVE the /:stateId? wrapper in every router (auth /
  // consumer / officer / organization). Otherwise a logged-in user
  // visiting /embed/documents falls through to their role-specific
  // Documents page via stateId="embed".

  // SSO v2 preview pages (PROD-72) — UI only, kept for demo/QA access.
  // The live routes are /login-sso/* (tenant-gated).
  {
    path: '/preview/sso-login',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <SsoLoginPreview />
      </Suspense>
    ),
  },
  {
    path: '/preview/sso-verify',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <SsoVerifyPreview />
      </Suspense>
    ),
  },

  // Stytch magic-link callback handler
  {
    path: '/auth/callback',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <StytchCallback />
      </Suspense>
    ),
  },
  // Branch portal callback (B2C shim redirects here)
  {
    path: '/branch/auth/callback',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <StytchCallback />
      </Suspense>
    ),
  },
  // Legacy / existing flows
  {
    path: '/setup-password',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyPassword />
      </Suspense>
    ),
  },
  {
    path: '/session-timeout',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <SessionExpire />
      </Suspense>
    ),
  },
  {
    path: '/verified',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <InviteExpire />
      </Suspense>
    ),
  },
  {
    path: '/result_page',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <EmailVerified />
      </Suspense>
    ),
  },
  {
    path: '/personal-info',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <PersonalInfoCard />
      </Suspense>
    ),
  },
  {
    path: '/verification-email',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback-v2/verification-email',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback/verification-email',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <VerifyEmail />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback-v2/personal-info',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <PersonalInfoCard />
      </Suspense>
    ),
  },
  {
    path: '/auth0-callback/personal-info',
    element: (
      <Suspense fallback={<Loader variant="gray" FullScreen={true} />}>
        <PersonalInfoCard />
      </Suspense>
    ),
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default authRoutes;
